import { useState } from "react";
import {
  Box,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { loadPerUserObjects } from "../../../services/assignmentImportService.js";
import { useSnackbar } from "notistack";

const formatStats = (stats) => {
  if (!stats || typeof stats !== "object")
  {
    return "–";
  }

  if (
    Object.prototype.hasOwnProperty.call(stats, "adds")
    || Object.prototype.hasOwnProperty.call(stats, "updates")
    || Object.prototype.hasOwnProperty.call(stats, "removes")
  )
  {
    const adds = stats.adds ?? 0;
    const updates = stats.updates ?? 0;
    const removes = stats.removes ?? 0;

    return `+${adds} / ~${updates} / -${removes}`;
  }

  if (
    Object.prototype.hasOwnProperty.call(stats, "restored_user_vertrag")
    || Object.prototype.hasOwnProperty.call(stats, "restored_user_objects")
    || Object.prototype.hasOwnProperty.call(stats, "deleted_user_vertrag")
    || Object.prototype.hasOwnProperty.call(stats, "deleted_user_objects")
  )
  {
    const restoredVertrag = stats.restored_user_vertrag ?? 0;
    const restoredObjects = stats.restored_user_objects ?? 0;
    const deletedVertrag = stats.deleted_user_vertrag ?? 0;
    const deletedObjects = stats.deleted_user_objects ?? 0;

    return `↩︎ UV ${restoredVertrag} · ↩︎ Obj ${restoredObjects} · ✖︎ UV ${deletedVertrag} · ✖︎ Obj ${deletedObjects}`;
  }

  return Object.entries(stats)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ")
    || "–";
};

const AssignmentImportPreview = ({ previewResult, kunde_id_ov, import_job_id }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [expanded_sections, set_expanded_sections] = useState({
    summary: true,
    errors: true,
    groupByUser: true,
  });

  const [show_all_warnings, set_show_all_warnings] = useState(false);
  const [pagination_dialog, set_pagination_dialog] = useState(null);
  const [paginated_objects, set_paginated_objects] = useState([]);
  const [current_page, set_current_page] = useState(1);
  const [total_pages, set_total_pages] = useState(1);
  const [loading_page, set_loading_page] = useState(false);
  const PAGE_SIZE = 20;

  const handle_section_toggle = (section) => {
    set_expanded_sections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handle_show_more_objects = async (user_id, vertrag_id, change_type, total) => {
    set_pagination_dialog({
      user_id,
      vertrag_id,
      change_type,
      total,
    });
    set_current_page(1);
    await load_page(user_id, vertrag_id, change_type, 1);
  };

  const load_page = async (user_id, vertrag_id, change_type, page) => {
    if (!kunde_id_ov || !import_job_id)
    {
      enqueueSnackbar("Kunde oder Import-Job nicht verfügbar", { variant: "error" });
      return;
    }

    set_loading_page(true);
    try
    {
      const offset = (page - 1) * PAGE_SIZE;
      const response = await loadPerUserObjects(
        kunde_id_ov,
        import_job_id,
        user_id,
        vertrag_id,
        change_type,
        offset,
        PAGE_SIZE
      );

      set_paginated_objects(response.objects || []);
      const total = response.pagination?.total || 0;
      set_total_pages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
      set_current_page(page);
    }
    catch (error)
    {
      enqueueSnackbar("Fehler beim Laden weiterer Objekte", { variant: "error" });
      console.error("Failed to load paginated objects:", error);
    }
    finally
    {
      set_loading_page(false);
    }
  };

  const handle_page_change = async (event, page) => {
    if (!pagination_dialog)
    {
      return;
    }

    await load_page(
      pagination_dialog.user_id,
      pagination_dialog.vertrag_id,
      pagination_dialog.change_type,
      page
    );
  };

  const handle_close_dialog = () => {
    set_pagination_dialog(null);
    set_paginated_objects([]);
    set_current_page(1);
    set_total_pages(1);
  };

  if (!previewResult) {
    return null;
  }

  const {
    validation_errors = [],
    business_errors = [],
    diff,
    stats = { adds: 0, removes: 0, updates: 0 },
    warnings = [],
    can_apply,
    snapshot_info = null,
    current_info = null,
    rollback_job = null,
  } = previewResult.preview ?? previewResult;
  const hydrated_diff = diff ?? {
    user_vertrag: { create: [], update_scope: [], delete: [] },
    user_objects: { add: [], remove: [] },
  };

  const user_contract_diff = hydrated_diff.user_vertrag ?? {};
  const user_object_diff = hydrated_diff.user_objects ?? {};
  const per_user_view = Array.isArray(hydrated_diff.per_user_view)
    ? hydrated_diff.per_user_view
    : [];

  const user_contract_changes = {
    create: (user_contract_diff.create ?? []).map((entry) => ({
      ...entry,
      assignment_scope_after:
        entry.assignment_scope_after ?? entry.assignment_scope ?? null,
    })),
    update: (user_contract_diff.update_scope ?? []).map((entry) => ({
      ...entry,
    })),
    delete: (user_contract_diff.delete ?? []).map((entry) => ({
      ...entry,
      assignment_scope_before:
        entry.assignment_scope_before ?? entry.assignment_scope ?? null,
    })),
  };

  const add_entries = Array.isArray(user_object_diff.add)
    ? user_object_diff.add
    : [];
  const remove_entries = Array.isArray(user_object_diff.remove)
    ? user_object_diff.remove
    : [];

  const user_object_changes = {
    add: add_entries,
    remove: remove_entries,
    add_total_count:
      Number.isFinite(Number(user_object_diff.add_total_count))
        ? Number(user_object_diff.add_total_count)
        : add_entries.length,
    remove_total_count:
      Number.isFinite(Number(user_object_diff.remove_total_count))
        ? Number(user_object_diff.remove_total_count)
        : remove_entries.length,
  };

  const has_errors = validation_errors.length > 0 || business_errors.length > 0;

  // Helper function to get scope display text
  const get_scope_display_text = (scope) => {
    switch (scope) {
      case "all":
      case "alle":
        return "Alle Objekte";
      case "selected":
      case "objektauswahl":
        return "Ausgewählte Objekte";
      default:
        return scope;
    }
  };

  // Helper texts for group headers (passive, neutral wording)
  const get_user_contract_change_text = (type, count) => {
    const obj = count === 1 ? "Zuordnung wird" : "Zuordnungen werden";
    switch (type) {
      case "create":
        return `${count} ${obj} angelegt`;
      case "update":
        return `${count} ${obj} aktualisiert`;
      case "delete":
        return `${count} ${obj} entfernt`;
      default:
        return `${count} Änderungen`;
    }
  };

  const get_user_object_change_text = (type, count) => {
    const obj = count === 1 ? "Objekt wird" : "Objekte werden";
    switch (type) {
      case "add":
        return `${count} ${obj} hinzugefügt`;
      case "remove":
        return `${count} ${obj} entfernt`;
      default:
        return `${count} Änderung${count === 1 ? "" : "en"}`;
    }
  };

  // Color coding for change types
  const get_change_color = (type) => {
    switch (type) {
      case "create":
      case "add":
        return "success.dark";
      case "update":
        return "warning.dark";
      case "delete":
      case "remove":
        return "error.dark";
      default:
        return "text.secondary";
    }
  };

  const format_user_summary = (summary) => {
    if (!summary || typeof summary !== "object")
    {
      return null;
    }

    const contracts_create = summary.contracts_create ?? 0;
    const contracts_update = summary.contracts_update ?? 0;
    const contracts_delete = summary.contracts_delete ?? 0;
    const objects_add_total = summary.objects_add_total ?? 0;
    const objects_remove_total = summary.objects_remove_total ?? 0;

    return `Vertragszuordnungen: +${contracts_create} / ~${contracts_update} / -${contracts_delete} · Objekt-Zuordnungen: +${objects_add_total} / -${objects_remove_total}`;
  };

  const render_contract_change_text = (contract_change) => {
    if (!contract_change || contract_change.type === "none")
    {
      return null;
    }

    switch (contract_change.type) {
      case "create":
        return `Vertragszuordnung wird angelegt: ${get_scope_display_text(contract_change.scope_after)}`;
      case "update":
        return `Vertragszuordnung wird aktualisiert: ${get_scope_display_text(contract_change.scope_before)} → ${get_scope_display_text(contract_change.scope_after)}`;
      case "delete":
        return `Vertragszuordnung wird entfernt: ${get_scope_display_text(contract_change.scope_before)}`;
      default:
        return null;
    }
  };

  const total_user_contract_changes =
    (user_contract_changes.create.length || 0) +
    (user_contract_changes.update.length || 0) +
    (user_contract_changes.delete.length || 0);

  const total_user_object_changes =
    (user_object_changes.add_total_count || 0) +
    (user_object_changes.remove_total_count || 0);

  return (
    <Box>
      {/* Summary Section */}
      <Accordion
        expanded={expanded_sections.summary}
        onChange={() => handle_section_toggle("summary")}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Änderungsübersicht</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Geplante Änderungen
            </Typography>
            <Typography variant="body2">
              Benutzer-Vertrag-Zuordnungen:{" "}
              <strong>{total_user_contract_changes}</strong> ·
              Objekt-Zuordnungen: <strong>{total_user_object_changes}</strong>
            </Typography>
            {warnings.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {(show_all_warnings ? warnings : warnings.slice(0, 3)).map(
                  (warning, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      color="warning.main"
                    >
                      {warning}
                    </Typography>
                  )
                )}
                {warnings.length > 3 && !show_all_warnings && (
                  <Button
                    size="small"
                    onClick={() => set_show_all_warnings(true)}
                    sx={{ mt: 0.5, textTransform: "none" }}
                  >
                    ... und {warnings.length - 3} weitere Hinweise anzeigen
                  </Button>
                )}
                {warnings.length > 3 && show_all_warnings && (
                  <Button
                    size="small"
                    onClick={() => set_show_all_warnings(false)}
                    sx={{ mt: 0.5, textTransform: "none" }}
                  >
                    Weniger anzeigen
                  </Button>
                )}
              </Box>
            )}
          </Alert>
          {snapshot_info && current_info && (
            <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
              <Box>
                <Typography variant="subtitle2">
                  Snapshot (vor Import)
                </Typography>
                <Typography variant="body2">
                  Benutzer-Verträge:{" "}
                  <strong>{snapshot_info.user_vertrag_count ?? 0}</strong>
                </Typography>
                <Typography variant="body2">
                  Objekt-Zuordnungen:{" "}
                  <strong>{snapshot_info.user_objects_count ?? 0}</strong>
                </Typography>
              </Box>
            </Box>
          )}
          {rollback_job && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Rollback-Auftrag #: {rollback_job.id} · Status:{" "}
                {rollback_job.status}
              </Typography>
              {rollback_job.stats && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Rollback-Statistik: {formatStats(rollback_job.stats)}
                </Typography>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Errors Section */}
      {has_errors && (
        <Accordion
          expanded={expanded_sections.errors}
          onChange={() => handle_section_toggle("errors")}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" color="error">
              Fehler ({validation_errors.length + business_errors.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {validation_errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Validierungsfehler ({validation_errors.length})
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {validation_errors.slice(0, 10).map((error, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                  {validation_errors.length > 10 && (
                    <li>
                      <Typography variant="body2" color="text.secondary">
                        ... und {validation_errors.length - 10} weitere Fehler
                      </Typography>
                    </li>
                  )}
                </Box>
              </Alert>
            )}

            {business_errors.length > 0 && (
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>
                  Geschäftsfehler ({business_errors.length})
                </Typography>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {business_errors.slice(0, 10).map((error, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                  {business_errors.length > 10 && (
                    <li>
                      <Typography variant="body2" color="text.secondary">
                        ... und {business_errors.length - 10} weitere Fehler
                      </Typography>
                    </li>
                  )}
                </Box>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Grouped by User: Show contract changes with nested object adds/removes */}
      {per_user_view.length > 0 && (
        <Accordion
          expanded={expanded_sections.groupByUser || false}
          onChange={() => handle_section_toggle("groupByUser")}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Änderungen pro Benutzer</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {per_user_view.map((user) => {
              const user_key = user.user_id ?? user.user_email ?? Math.random();
              const summary_text = format_user_summary(user.summary);

              return (
                <Box key={user_key} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    <strong>
                      {user.user_name || user.user_email || `Benutzer ${user.user_id}`}
                    </strong>
                  </Typography>
                  {user.user_email && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      {user.user_email}
                    </Typography>
                  )}

                  {summary_text && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {summary_text}
                    </Typography>
                  )}

                  {Array.isArray(user.contracts) && user.contracts.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Keine Vertragszuordnungen für diesen Benutzer.
                    </Alert>
                  )}

                  {Array.isArray(user.contracts) &&
                    user.contracts.map((contract) => {
                      const contract_key = `${user.user_id}-${contract.vertrag_id}`;
                      const contract_change_text = render_contract_change_text(
                        contract.contract_change
                      );
                      const add_bucket = contract.objects?.add ?? {};
                      const remove_bucket = contract.objects?.remove ?? {};
                      const add_items = Array.isArray(add_bucket.items)
                        ? add_bucket.items
                        : [];
                      const remove_items = Array.isArray(remove_bucket.items)
                        ? remove_bucket.items
                        : [];
                      const add_total = Number.isFinite(Number(add_bucket.total))
                        ? Number(add_bucket.total)
                        : add_items.length;
                      const remove_total = Number.isFinite(Number(remove_bucket.total))
                        ? Number(remove_bucket.total)
                        : remove_items.length;
                      const has_more_add = add_total > add_items.length;
                      const has_more_remove = remove_total > remove_items.length;

                      return (
                        <Box key={contract_key} sx={{ pl: 2, mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Vertrag: <strong>{contract.vertrag_label || contract.vertrag_id}</strong>
                            {contract.vertrag_id_ov && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (ID: {contract.vertrag_id_ov})
                              </Typography>
                            )}
                          </Typography>

                          {contract_change_text && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: get_change_color(contract.contract_change?.type),
                                mb: 1,
                              }}
                            >
                              {contract_change_text}
                            </Typography>
                          )}

                          {["add", "remove"].map((type) => {
                            const bucket = contract.objects?.[type];
                            if (!bucket)
                            {
                              return null;
                            }

                            const items = Array.isArray(bucket.items) ? bucket.items : [];
                            const total = Number.isFinite(Number(bucket.total))
                              ? Number(bucket.total)
                              : items.length;

                            if (items.length === 0 && total === 0)
                            {
                              return null;
                            }

                            const has_more = total > items.length;

                            return (
                              <Box
                                key={`${contract_key}-${type}`}
                                sx={{ mb: 1, pl: 2 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: get_change_color(type) }}
                                >
                                  {get_user_object_change_text(type, total)}
                                </Typography>
                                {items.length > 0 && (
                                  <Box
                                    component="ul"
                                    sx={{ margin: 0, paddingLeft: 2 }}
                                  >
                                    {items.map((obj, idx) => (
                                      <li key={idx}>
                                        <Typography variant="caption">
                                          {obj.objekt_bezeichnung ||
                                            `Objekt ${obj.object_id}`}
                                          {obj.objekt_id_ov && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ ml: 0.5 }}
                                            >
                                              (ID: {obj.objekt_id_ov})
                                            </Typography>
                                          )}
                                          {obj.implicit_scope_all && (
                                            <Typography
                                              component="span"
                                              variant="caption"
                                              color="info.main"
                                              sx={{ ml: 0.5 }}
                                            >
                                              (implizit durch "Alle")
                                            </Typography>
                                          )}
                                        </Typography>
                                      </li>
                                    ))}
                                  </Box>
                                )}
                                {has_more && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handle_show_more_objects(
                                      user.user_id,
                                      contract.vertrag_id,
                                      type,
                                      total
                                    )}
                                    sx={{ textTransform: "none", p: 0 }}
                                  >
                                    ... und {total - items.length} weitere Objekte anzeigen
                                  </Button>
                                )}
                              </Box>
                            );
                          })}

                          {add_total === 0 && remove_total === 0 && !contract_change_text && (
                            <Typography variant="caption" color="text.secondary">
                              Keine Änderungen für diesen Vertrag.
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Pagination Dialog */}
      <Dialog
        open={Boolean(pagination_dialog)}
        onClose={handle_close_dialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {pagination_dialog && (
            <>
              Objekte ({pagination_dialog.total}){" "}
              {pagination_dialog.change_type === "add" ? "hinzugefügt" : "entfernt"}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {loading_page ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense>
              {paginated_objects.map((obj, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={obj.objekt_bezeichnung || `Objekt ${obj.object_id}`}
                    secondary={
                      <>
                        {obj.objekt_id_ov && `ID: ${obj.objekt_id_ov}`}
                        {obj.implicit_scope_all && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="info.main"
                            sx={{ ml: 1 }}
                          >
                            (implizit durch "Alle")
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {paginated_objects.length === 0 && !loading_page && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Keine Objekte gefunden.
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Pagination
            count={total_pages}
            page={current_page}
            onChange={handle_page_change}
            showFirstButton
            showLastButton
            disabled={loading_page}
          />
          <Button onClick={handle_close_dialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignmentImportPreview;
